import { z } from 'zod';
import { AppError } from './errors/base.ts';

/** A nominal-typed string so an `AccountId` can never be passed where a `UserId` is wanted. */
export type Brand<T, B extends string> = T & { readonly __brand: B };

/**
 * Thrown when a string is asserted to be a branded id but is not a valid UUID v7.
 *
 * The offending VALUE is deliberately not in the message. This error is raised
 * straight off path params and body fields, renders as a `400` envelope, and is
 * therefore reachable by anyone with a URL bar — echoing the input back made every
 * id-taking route a reflection sink for arbitrary attacker bytes. Which field and
 * which brand failed is all a legitimate client needs; the value it sent, it
 * already has.
 */
export class InvalidIdError extends AppError {
  readonly code = 'invalid_id';
  constructor(brand: string) {
    super(`Invalid ${brand}: expected a UUID v7`, { statusCode: 400 });
  }
}

const UUID_V7_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** True when `value` is a well-formed UUID v7 string. */
export function isUuidV7(value: string): boolean {
  return UUID_V7_RE.test(value);
}

// Monotonic state so ids minted within the same millisecond still sort in
// creation order (the 12-bit `rand_a` field carries a per-ms sequence).
let lastMs = -1;
let seq = 0;

/**
 * Mint a UUID v7 — a 48-bit Unix-ms timestamp prefix makes it time-sortable and
 * range-friendly (ideal as a Yugabyte primary key). App-side only, never in-DB.
 */
export function uuid(): string {
  let ms = Date.now();
  if (ms < lastMs) {
    // Clock moved backwards — pin to the last timestamp to preserve ordering.
    ms = lastMs;
  }
  if (ms === lastMs) {
    seq += 1;
    if (seq > 0xfff) {
      ms += 1;
      seq = 0;
    }
  } else {
    seq = 0;
  }
  lastMs = ms;

  const view = new DataView(new ArrayBuffer(16));
  view.setUint16(0, Math.floor(ms / 2 ** 32) & 0xffff);
  view.setUint32(2, ms >>> 0);
  view.setUint8(6, 0x70 | ((seq >>> 8) & 0x0f));
  view.setUint8(7, seq & 0xff);

  const rnd = new DataView(crypto.getRandomValues(new Uint8Array(8)).buffer);
  view.setUint8(8, 0x80 | (rnd.getUint8(0) & 0x3f));
  view.setUint8(9, rnd.getUint8(1));
  view.setUint32(10, rnd.getUint32(2));
  view.setUint16(14, rnd.getUint16(6));

  let hex = '';
  for (let i = 0; i < 16; i += 1) {
    hex += view.getUint8(i).toString(16).padStart(2, '0');
  }
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * A Zod schema that validates a UUID v7 string and brands its output, so a shared
 * contract can reject a malformed id at the wire boundary (not at a later use
 * site). Mirrors `<Brand>.of()`'s check inside `safeParse`, e.g.
 * `girlfriendId: brandedUuid('GirlfriendId')`.
 */
export function brandedUuid<B extends string>(brand: B) {
  return z
    .string()
    .refine(isUuidV7, { message: `${brand} must be a UUID v7` })
    .transform((value) => value as Brand<string, B>);
}

/** Factory for a branded-id type: `.make()` mints a fresh id, `.of()` validates an existing string. */
function brandedId<B extends string>(brand: B) {
  return {
    make(): Brand<string, B> {
      return uuid() as Brand<string, B>;
    },
    of(value: string): Brand<string, B> {
      if (!isUuidV7(value)) {
        throw new InvalidIdError(brand);
      }
      return value as Brand<string, B>;
    },
  };
}

// One branded id per id-bearing table in docs/idea/data-model.md. Each is both a
// type (`AccountId`) and a runtime factory (`AccountId.make()` / `AccountId.of()`).

// Identity & accounts
export type AccountId = Brand<string, 'AccountId'>;
export const AccountId = brandedId('AccountId');

export type UserId = Brand<string, 'UserId'>;
export const UserId = brandedId('UserId');

// Companions & chat
export type GirlfriendId = Brand<string, 'GirlfriendId'>;
export const GirlfriendId = brandedId('GirlfriendId');

export type ConversationId = Brand<string, 'ConversationId'>;
export const ConversationId = brandedId('ConversationId');

export type MessageId = Brand<string, 'MessageId'>;
export const MessageId = brandedId('MessageId');

// Memory
export type MemoryId = Brand<string, 'MemoryId'>;
export const MemoryId = brandedId('MemoryId');

// Media
export type MediaJobId = Brand<string, 'MediaJobId'>;
export const MediaJobId = brandedId('MediaJobId');

export type MediaSegmentId = Brand<string, 'MediaSegmentId'>;
export const MediaSegmentId = brandedId('MediaSegmentId');

export type MediaAssetId = Brand<string, 'MediaAssetId'>;
export const MediaAssetId = brandedId('MediaAssetId');

// Billing & credits
export type SubscriptionId = Brand<string, 'SubscriptionId'>;
export const SubscriptionId = brandedId('SubscriptionId');

export type CreditLedgerId = Brand<string, 'CreditLedgerId'>;
export const CreditLedgerId = brandedId('CreditLedgerId');

export type UsageEventId = Brand<string, 'UsageEventId'>;
export const UsageEventId = brandedId('UsageEventId');

// Referrals
export type ReferralId = Brand<string, 'ReferralId'>;
export const ReferralId = brandedId('ReferralId');

export type ReferralEarningId = Brand<string, 'ReferralEarningId'>;
export const ReferralEarningId = brandedId('ReferralEarningId');

// Anti-fraud
export type DeviceFingerprintId = Brand<string, 'DeviceFingerprintId'>;
export const DeviceFingerprintId = brandedId('DeviceFingerprintId');

// Staff / admin
export type StaffUserId = Brand<string, 'StaffUserId'>;
export const StaffUserId = brandedId('StaffUserId');

export type StaffAuditLogId = Brand<string, 'StaffAuditLogId'>;
export const StaffAuditLogId = brandedId('StaffAuditLogId');

// Proactive messaging
export type ProactiveScheduleId = Brand<string, 'ProactiveScheduleId'>;
export const ProactiveScheduleId = brandedId('ProactiveScheduleId');

export type PushSubscriptionId = Brand<string, 'PushSubscriptionId'>;
export const PushSubscriptionId = brandedId('PushSubscriptionId');

// Engagement / achievements
export type AchievementId = Brand<string, 'AchievementId'>;
export const AchievementId = brandedId('AchievementId');

export type UserAchievementId = Brand<string, 'UserAchievementId'>;
export const UserAchievementId = brandedId('UserAchievementId');

// Moderation
export type ModerationEventId = Brand<string, 'ModerationEventId'>;
export const ModerationEventId = brandedId('ModerationEventId');
